import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, LoginCredentials, RegisterData, AuthContextType } from '../types';
import { firebaseAuthService } from '../services/firebaseAuthService';
import { queryClient } from '../lib/queryClient';
import toast from 'react-hot-toast';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMasquerading, setIsMasquerading] = useState(false);
  const verifyingRef = useRef(false);
  const lastVerifiedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // Check if we are masquerading on load
    const returnToken = localStorage.getItem('masquerade_return_token');
    setIsMasquerading(!!returnToken);

    // On first load, avoid sending obviously stale tokens to the backend.
    // Prefer the live Firebase ID token if a user session exists; otherwise clear any stale local token
    const bootstrapAuthState = async () => {
      try {
        const currentFirebaseUser = firebaseAuthService.getCurrentFirebaseUser();
        const storedToken = localStorage.getItem('token');

        if (currentFirebaseUser) {
          // Firebase user exists - get fresh token and verify
          try {
            const freshToken = await currentFirebaseUser.getIdToken();
            if (freshToken && freshToken !== lastVerifiedTokenRef.current) {
              lastVerifiedTokenRef.current = freshToken;
              setToken(freshToken);
              await verifyToken(freshToken);
              return;
            }
          } catch (error) {
            console.warn('AuthContext - Error getting Firebase token:', error);
            // Fall through to check stored token
          }
        }

        if (storedToken && storedToken !== lastVerifiedTokenRef.current) {
          // No Firebase session, but we have a stored token - try to verify it
          // This handles cases where Firebase hasn't initialized yet or session needs restoration
          try {
            lastVerifiedTokenRef.current = storedToken;
            await verifyToken(storedToken);
            // If verification succeeds, we're good
            return;
          } catch (error: any) {
            console.warn('AuthContext - Token verification failed:', error?.message || error);
            // Only clear token if it's a 401/403 error (unauthorized)
            if (error?.response?.status === 401 || error?.response?.status === 403) {
              localStorage.removeItem('token');
              setToken(null);
              setUser(null);
              lastVerifiedTokenRef.current = null;
            }
            // For other errors, keep the token - might be a network issue
          }
        }
      } catch (e) {
        console.warn('AuthContext - Bootstrap error:', e);
      } finally {
        setIsLoading(false);
      }
    };

    bootstrapAuthState();

    // Listen to Firebase auth state changes (for Google sign-in, etc.)
    const unsubscribe = firebaseAuthService.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        // User signed in (could be email/password or Google)
        try {
          const token = await firebaseUser.getIdToken();
          if (token && token !== lastVerifiedTokenRef.current && !verifyingRef.current) {
            verifyingRef.current = true;
            lastVerifiedTokenRef.current = token;
            try {
              await verifyToken(token);
            } finally {
              verifyingRef.current = false;
            }
          }
        } catch (error) {
          console.error('AuthContext - Error getting token from Firebase user:', error);
          verifyingRef.current = false;
        }
      } else {
        // User signed out - clear React Query cache to prevent data leakage between users
        queryClient.clear();
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        lastVerifiedTokenRef.current = null;
        setIsLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const verifyToken = async (token: string) => {
    // Prevent duplicate verification calls
    if (verifyingRef.current && lastVerifiedTokenRef.current === token) {
      return;
    }

    verifyingRef.current = true;
    try {
      const { user: userData, freshToken } = await firebaseAuthService.getCurrentUser(token);
      const tokenToSave = freshToken || token;

      // If we have a different user than before, clear the cache
      // This handles the case where a user switches accounts without logging out first
      const currentUserId = userData.id;
      const previousUserId = user?.id;
      if (previousUserId && currentUserId !== previousUserId) {
        queryClient.clear();
      }

      setUser(userData);
      setToken(tokenToSave);
      lastVerifiedTokenRef.current = tokenToSave;
      // Save token to localStorage for API interceptor (with error handling)
      try {
        localStorage.setItem('token', tokenToSave);
      } catch (storageError) {
        // Handle localStorage errors (e.g., quota exceeded, private browsing)
        console.warn('AuthContext - Failed to save token to localStorage:', storageError);
        toast.error('Unable to save your session. You may need to log in again after closing your browser. Please check your browser\'s storage or privacy settings.');
      }
    } catch (error: any) {
      console.error('AuthContext - Authentication error:', error?.message || error);

      // Only clear token if it's a 401/403 error (unauthorized)
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        queryClient.clear();
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        lastVerifiedTokenRef.current = null;
      } else {
        // For other errors (network, etc.), keep the token but don't set user
        setToken(token);
        localStorage.setItem('token', token); // Save token even if verification had non-auth errors
        // Don't set user if verification failed for non-auth reasons
        // This will keep the user logged in but they might need to refresh
      }
    } finally {
      verifyingRef.current = false;
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      // Clear React Query cache on login to ensure fresh data for the new user
      queryClient.clear();
      const response = await firebaseAuthService.login(credentials);

      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('token', response.token);
      lastVerifiedTokenRef.current = response.token;

      toast.success('Login successful!');

      // Return the response so the Login component can handle navigation
      return response;
    } catch (error: any) {
      console.error('AuthContext login - error:', error);
      toast.error(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setIsLoading(true);
      // Clear React Query cache on login to ensure fresh data for the new user
      queryClient.clear();
      const response = await firebaseAuthService.loginWithGoogle();

      // Immediately update AuthContext state so navigation works
      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('token', response.token);
      lastVerifiedTokenRef.current = response.token;

      toast.success('Login successful!');

      // Return the response so the Login component can handle navigation
      return response;
    } catch (error: any) {
      console.error('AuthContext loginWithGoogle - error:', error);
      toast.error(error.message || 'Google sign-in failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const completeMfaLogin = async (mfaToken: string) => {
    try {
      setIsLoading(true);
      // Clear React Query cache on login to ensure fresh data for the new user
      queryClient.clear();
      const response = await firebaseAuthService.completeLoginWithMfa(mfaToken);

      // Immediately update AuthContext state so navigation works
      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('token', response.token);
      lastVerifiedTokenRef.current = response.token;

      toast.success('Login successful!');

      // Return the response so the Login component can handle navigation
      return response;
    } catch (error: any) {
      console.error('AuthContext completeMfaLogin - error:', error);
      toast.error(error.message || 'MFA login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterData) => {
    try {
      setIsLoading(true);
      const response = await firebaseAuthService.register(data);

      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('token', response.token);

      toast.success('Registration successful!');

      // Return the response so the Register component can handle navigation
      return response;
    } catch (error: any) {
      toast.error(error.message || 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const masqueradeAsUser = async (userId: number) => {
    try {
      setIsLoading(true);
      // 1. Call backend to get tokens
      const { targetToken, adminToken } = await firebaseAuthService.masquerade(userId);

      // 2. Store admin return token
      localStorage.setItem('masquerade_return_token', adminToken);
      setIsMasquerading(true);

      // 3. Sign in as target user
      const { user: newUser, token: newToken } = await firebaseAuthService.signInWithCustomToken(targetToken);

      // 4. Update state
      queryClient.clear();
      setUser(newUser);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      lastVerifiedTokenRef.current = newToken;

      toast.success(`Now masquerading as ${newUser.firstName} ${newUser.lastName}`);
      // Reload page to ensure clean state
      window.location.href = '/dashboard';
    } catch (error: any) {
      console.error('Masquerade error:', error);
      toast.error(error.message || 'Failed to masquerade');
      setIsMasquerading(false);
      localStorage.removeItem('masquerade_return_token');
    } finally {
      setIsLoading(false);
    }
  };

  const stopMasquerade = async () => {
    try {
      const returnToken = localStorage.getItem('masquerade_return_token');
      if (!returnToken) {
        throw new Error('No return session found');
      }

      setIsLoading(true);

      // 1. Sign in with the admin return token
      const { user: adminUser, token: newToken } = await firebaseAuthService.signInWithCustomToken(returnToken);

      // 2. Clear masquerade state
      localStorage.removeItem('masquerade_return_token');
      setIsMasquerading(false);

      // 3. Update state
      queryClient.clear();
      setUser(adminUser);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      lastVerifiedTokenRef.current = newToken;

      toast.success('Returned to Admin session');
      // Reload page to ensure clean state
      window.location.href = '/users';
    } catch (error: any) {
      console.error('Stop masquerade error:', error);
      toast.error('Failed to return to admin session. Please log in again.');
      // Prevent recursive prompt loops when the return token is invalid.
      await performLogout(true);
    } finally {
      setIsLoading(false);
    }
  };

  const clearLocalSession = () => {
    queryClient.clear();
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('masquerade_return_token');
    setIsMasquerading(false);
    lastVerifiedTokenRef.current = null;
  };

  const performLogout = async (skipMasqueradePrompt = false) => {
    // Intercept logout if masquerading
    if (!skipMasqueradePrompt && localStorage.getItem('masquerade_return_token')) {
      if (window.confirm('You are currently masquerading. Do you want to return to your Admin session instead of logging out?')) {
        await stopMasquerade();
        return;
      }
      // If they say no, proceed to full logout and clear return token
      localStorage.removeItem('masquerade_return_token');
      setIsMasquerading(false);
    }

    try {
      await firebaseAuthService.logout();
      clearLocalSession();
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state and cache even if Firebase logout fails
      clearLocalSession();
    }
  };

  const logout = async () => {
    await performLogout(false);
  };

  const isAuthenticated = !!user && !!token;

  const value: AuthContextType = {
    user,
    token,
    login,
    loginWithGoogle,
    completeMfaLogin,
    register,
    logout,
    isLoading,
    isAuthenticated,
    masqueradeAsUser,
    stopMasquerade,
    isMasquerading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
